import { Component, Input, ChangeDetectorRef, OnChanges, Output, EventEmitter, SimpleChanges, ViewChild, ViewContainerRef, ComponentFactoryResolver, ComponentRef, OnDestroy } from '@angular/core';
import { ErrorLoggerService, NotificationService } from 'app/services';
import { TranslateService } from '@ngx-translate/core';
import { InlineListSectionHandler, InlineListItem } from '../../entities/shared/inline-list.section';

@Component({
    selector: 'key-admin-inline-list-editor',
    templateUrl: './inline-list-editor.component.html',
    styleUrls: ['./inline-list-editor.component.scss'],
    standalone: false
})
export class AdminInlineListEditorComponent implements OnChanges, OnDestroy {

    @Input() entity: any;
    @Input() handler: InlineListSectionHandler<any>;
    @Input() editable: boolean;

    @Output() onEditing = new EventEmitter<boolean>();
    @Output() onSaving = new EventEmitter<any>();
    @Output() onSaved = new EventEmitter<any>();

    @ViewChild('templateContainer', { read: ViewContainerRef, static: true }) container: ViewContainerRef;

    errorMessage: string;
    items: InlineListItem[];

    componentRefs: ComponentRef<any>[] = [];

    autoReloadInterval = null;

    constructor(
        private error: ErrorLoggerService,
        private notify: NotificationService,
        public i18n: TranslateService,
        private ref: ChangeDetectorRef,
        private resolver: ComponentFactoryResolver
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.handler && this.handler) {
            this.load().catch(err => this.showError(err));
        }
    }

    ngOnDestroy() {
        if (this.autoReloadInterval) {
            clearInterval(this.autoReloadInterval);
        }
        this.clear();
    }

    async load(): Promise<void> {
        try {
            this.errorMessage = null;
            if (this.autoReloadInterval) {
                clearInterval(this.autoReloadInterval);
                this.autoReloadInterval = null;
            }
            this.clear();
            this.refresh();
            if (this.handler.autoReloadSeconds) {
                this.autoReloadInterval = setInterval(() => this.refresh(), this.handler.autoReloadSeconds * 1000);
            }
        } catch (err) {
            this.showError(err);
        }
    }

    async refresh() {
        try {
            if (this.entity) {
                const items = await this.handler.getItems(this.entity, 0, 100);
                this.errorMessage = null;
                if (this.handler.getComponentFactory) {
                    const factory = this.handler.getComponentFactory(this.resolver);
                    items.forEach(item => {
                        let ref = this.componentRefs.find(x => x.instance.item.id === item.id);
                        if (!ref) {
                            ref = this.container.createComponent(factory);
                            this.componentRefs.push(ref);
                        }
                        ref.instance.item = item;
                        if (ref.instance.ngOnChanges) {
                            ref.instance.ngOnChanges();
                        }
                    });
                    this.componentRefs.forEach((componentRef, index) => {
                        const id = componentRef.instance.item.id;
                        if (!items.find(x => x.id === id)) {
                            this.componentRefs[index] = null;
                            componentRef.destroy();
                        }
                    });
                    this.componentRefs = this.componentRefs.filter(x => x);
                } else {
                    this.items = items;
                }
            }
            this.ref.markForCheck();
        } catch (err) {
            this.showError(err);
        }
    }

    clear() {
        this.items = null;
        this.container.clear();
        this.componentRefs.forEach(componentRef => componentRef.destroy());
        this.componentRefs = [];
    }

    showError(error: any) {
        this.error.trackException(error);
        this.errorMessage = error && this.notify.translateError(error);
        this.ref.markForCheck();
    }


    async onSelected(item: InlineListItem) {
        this.ref.markForCheck();
        this.entity = await this.handler.actionClicked('click', this.entity, item) || this.entity;
        await this.load();
    }

    async actionClicked(action: string, item: InlineListItem) {
        this.entity = await this.handler.actionClicked(action, this.entity, item) || this.entity;
        this.load();
    }

    async onDelete(item: InlineListItem) {
        this.entity = await this.handler.actionClicked('delete', this.entity, item) || this.entity;
        await this.load();
    }
}
